import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "../services/api";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
  Stack,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import EventNoteIcon from "@mui/icons-material/EventNote";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";

const REFRESH_MS = 30_000;

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
// Prices can carry more than 2 decimals (e.g. 1.185) — show them exactly,
// up to 4 decimal places, instead of rounding to cents.
const fmtPrice = (n) =>
  "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const fmtNum = (n) => Number(n || 0).toLocaleString();
const fmtDate = (d) => {
  if (!d) return "-";
  // Date-only columns from MSSQL arrive as "YYYY-MM-DDT00:00:00Z". Parsing that
  // with `new Date(...)` in a negative-UTC timezone rolls back a day, so build
  // the Date from the local Y/M/D parts instead.
  const ymd = String(d).slice(0, 10).split("-");
  if (ymd.length === 3) {
    const [y, m, dd] = ymd.map(Number);
    if (y && m && dd) return new Date(y, m - 1, dd).toLocaleDateString();
  }
  return new Date(d).toLocaleDateString();
};

/* ================= SUMMARY TILE ================= */
function Tile({ icon, title, value, sub, color }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        height: "100%",
        display: "flex",
        alignItems: "center",
        gap: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        transition: "box-shadow .2s ease, transform .2s ease",
        "&:hover": { boxShadow: "0 8px 24px rgba(16,24,40,.10)", transform: "translateY(-2px)" },
      }}
    >
      <Box
        sx={{
          width: 52, height: 52, borderRadius: 2, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          color, bgcolor: alpha(color, 0.12),
          "& svg": { fontSize: 28 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary", textTransform: "uppercase",
            letterSpacing: 0.3, fontWeight: 600, lineHeight: 1.2, display: "block",
          }}
        >
          {title}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
          {value}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {sub}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

/* ================= LAZY ACCORDION ================= */
function LazyAccordion({ title, count, headers, fetchUrl, mapRow, emptyText }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(fetchUrl);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(`Failed ${fetchUrl}:`, err);
      setRows([]);
    }
  }, [fetchUrl]);

  const handleChange = (_, expanded) => {
    setOpen(expanded);
    if (expanded) load();
  };

  return (
    <Accordion expanded={open} onChange={handleChange}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          {title} ({count})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {rows === null ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography align="center" sx={{ color: "gray" }}>
            {emptyText}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {headers.map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i}>
                    {mapRow(r).map((cell, j) => (
                      <TableCell key={j}>{cell}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

/* ================= INVENTORY PANEL (per-product, searchable) ================= */
function InventoryPanel() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/dashboard/inventory");
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed inventory:", err);
      setRows([]);
    }
  }, []);

  const handleChange = (_, expanded) => {
    setOpen(expanded);
    if (expanded) load();
  };

  const filtered = (rows || []).filter((r) =>
    (r.Item_Description || "").toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <Accordion expanded={open} onChange={handleChange}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          Inventory — Last 7 Days by Product
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <TextField
          placeholder="Search product (e.g. battery)…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          onClick={(e) => e.stopPropagation()}
        />

        {rows === null ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography align="center" sx={{ color: "gray" }}>
            {rows.length === 0
              ? "No purchase or sale activity in the last 30 days"
              : "No products match your search"}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Total Net Wt In</TableCell>
                  <TableCell align="right">Avg Cost</TableCell>
                  <TableCell align="right">Total Cost</TableCell>
                  <TableCell align="right">Total Net Wt Out</TableCell>
                  <TableCell align="right">Avg Sale Price</TableCell>
                  <TableCell align="right">Total Revenue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r, i) => (
                  <TableRow key={r.Item_Description + i} hover>
                    <TableCell>{r.Item_Description}</TableCell>
                    <TableCell align="right">{fmtNum(r.QtyIn)}</TableCell>
                    <TableCell align="right">{fmtMoney(r.AvgCost)}</TableCell>
                    <TableCell align="right">{fmtMoney(r.CostIn)}</TableCell>
                    <TableCell align="right">{fmtNum(r.QtyOut)}</TableCell>
                    <TableCell align="right">{fmtMoney(r.AvgPrice)}</TableCell>
                    <TableCell align="right">{fmtMoney(r.RevenueOut)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

/* ============= STOCK INVENTORY PANEL (current balance, searchable) ============= */
function StockInventoryPanel() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/dashboard/stockInventory");
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed stock inventory:", err);
      setRows([]);
    }
  }, []);

  const handleChange = (_, expanded) => {
    setOpen(expanded);
    if (expanded) load();
  };

  const filtered = (rows || []).filter((r) =>
    (r.Item_Description || "").toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <Accordion expanded={open} onChange={handleChange}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          Stock Inventory — Current Balance by Product
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <TextField
          placeholder="Search product (e.g. battery)…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          onClick={(e) => e.stopPropagation()}
        />

        {rows === null ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography align="center" sx={{ color: "gray" }}>
            {rows.length === 0
              ? "No stock activity recorded yet"
              : "No products match your search"}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Balance (lb)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r, i) => (
                  <TableRow key={r.Item_Description + i} hover>
                    <TableCell>{r.Item_Description}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtNum(r.Balance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

/* ================= MAIN DASHBOARD ================= */
export default function Dashboard({ onNavigate }) {
  const [summary, setSummary] = useState({});

  // Effective module list for the logged-in company (set at login / /auth/me).
  // When a module is disabled for the company, its dashboard widget is hidden
  // too. If there is no list (legacy/single-tenant), show everything.
  const has = useMemo(() => {
    let modules = null;
    try {
      const m = JSON.parse(localStorage.getItem("user") || "{}").modules;
      modules = Array.isArray(m) ? m : null;
    } catch { modules = null; }
    return (key) => !modules || modules.includes(key);
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get("/dashboard/summary");
      setSummary(res.data || {});
    } catch (err) {
      console.error("Failed to fetch summary:", err);
    }
  }, []);

  useEffect(() => {
    let id = null;

    const startInterval = () => {
      if (id) return;
      id = setInterval(fetchSummary, REFRESH_MS);
    };
    const stopInterval = () => {
      if (!id) return;
      clearInterval(id);
      id = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchSummary();         // catch up on whatever changed while hidden
        startInterval();
      } else {
        stopInterval();         // don't waste battery / mobile data in background
      }
    };

    fetchSummary();
    if (document.visibilityState === "visible") startInterval();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stopInterval();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchSummary]);

  const tiles = [
    {
      icon: <ReceiptLongIcon fontSize="inherit" />,
      title: "Active Sale Orders",
      value: fmtNum(summary.SaleOrders),
      color: "#1976d2",
    },
    {
      icon: <ShoppingCartIcon fontSize="inherit" />,
      title: "Active Purchase Orders",
      value: fmtNum(summary.PurchaseOrders),
      color: "#9c27b0",
    },
    has("booking") && {
      icon: <EventNoteIcon fontSize="inherit" />,
      title: "Active Bookings",
      value: fmtNum(summary.Bookings),
      color: "#2e7d32",
    },
    has("container") && {
      icon: <LocalShippingIcon fontSize="inherit" />,
      title: "Active Containers",
      value: fmtNum(summary.Containers),
      color: "#ed6c02",
    },
    has("pickup") && {
      icon: <EventAvailableIcon fontSize="inherit" />,
      title: "Scheduled Pickups",
      value: fmtNum(summary.Pickups),
      color: "#0288d1",
    },
  ].filter(Boolean);

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Title on the left, the two price shortcuts on the right. They wrap
        * underneath on a phone rather than squashing the heading. */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                 flexWrap: "wrap", gap: 1, mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          Dashboard
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="outlined" size="small" startIcon={<ShowChartIcon />}
            onClick={() => onNavigate && onNavigate("PRICE DASHBOARD")}
          >
            Price Dashboard
          </Button>
          <Button
            variant="contained" size="small" startIcon={<PriceChangeIcon />}
            onClick={() => onNavigate && onNavigate("GLOMETS PRICE LIST")}
          >
            Glomets Price List
          </Button>
        </Stack>
      </Box>

      {/* ================= SUMMARY TILES ================= */}
      <Grid container spacing={2} mb={3}>
        {tiles.map((t) => (
          <Grid key={t.title} size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
            <Tile {...t} />
          </Grid>
        ))}
      </Grid>

      {/* ================= DETAIL ACCORDIONS (one per row) ================= */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <LazyAccordion
            title="Active Sale Orders"
            count={summary.SaleOrders || 0}
            fetchUrl="/dashboard/saleOrders"
            headers={["SO #", "Customer", "Item", "Ordered", "Loaded", "Remaining", "Price", "Date"]}
            mapRow={(r) => [
              r.SO,
              r.Name,
              r.Item_Description,
              r.Qty,
              r.Loaded,
              r.Remaining,
              r.Price === null || r.Price === undefined ? "-" : fmtPrice(r.Price),
              fmtDate(r.Date),
            ]}
            emptyText="No Active Sale Orders"
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <LazyAccordion
            title="Active Purchase Orders"
            count={summary.PurchaseOrders || 0}
            fetchUrl="/dashboard/purchaseOrders"
            headers={["PO #", "Vendor", "Item", "Qty", "Date"]}
            mapRow={(r) => [
              r.SO,
              r.Name,
              r.Item_Description,
              r.Qty,
              fmtDate(r.Date),
            ]}
            emptyText="No Active Purchase Orders"
          />
        </Grid>

        {has("booking") && (
        <Grid size={{ xs: 12 }}>
          <LazyAccordion
            title="Active Bookings"
            count={summary.Bookings || 0}
            fetchUrl="/dashboard/bookings"
            headers={["Booking #", "ERD Date", "Doc Cut Off", "Cargo Cut Off", "PO", "Item", "Ordered", "Loaded", "Remaining"]}
            mapRow={(r) => [
              r.BookingNo,
              fmtDate(r.ERDDate),
              fmtDate(r.DOCCutOff),
              fmtDate(r.CargoCutOff),
              r.PO,
              r.Item_Description,
              fmtNum(r.Qty),
              fmtNum(r.Loaded),
              fmtNum(r.Remaining),
            ]}
            emptyText="No Active Bookings"
          />
        </Grid>
        )}

        {has("container") && (
        <Grid size={{ xs: 12 }}>
          <LazyAccordion
            title="Active Containers"
            count={summary.Containers || 0}
            fetchUrl="/dashboard/containers"
            headers={["Container #", "Booking #", "PO", "Products"]}
            mapRow={(r) => [
              r.ContainerNo,
              r.BookingNo,
              r.POs || "-",
              r.Items || "-",
            ]}
            emptyText="No Active Containers"
          />
        </Grid>
        )}

        {has("pickup") && (
        <Grid size={{ xs: 12 }}>
          <LazyAccordion
            title="Scheduled Pickups"
            count={summary.Pickups || 0}
            fetchUrl="/dashboard/pickups"
            headers={["Pickup Date", "Time", "SO #", "Customer", "Item", "Loads", "Driver / Carrier"]}
            mapRow={(r) => [
              fmtDate(r.PickupDate),
              r.PickupTime || "-",
              r.SO ? `SO# ${r.SO}` : "-",
              r.Customer_Name || "-",
              r.Item_Description || "-",
              fmtNum(r.Qty),
              r.DriverName || "-",
            ]}
            emptyText="No Scheduled Pickups"
          />
        </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          <InventoryPanel />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <StockInventoryPanel />
        </Grid>
      </Grid>
    </Box>
  );
}
